/**
 * Tilaukset -sivu (refaktoroitu)
 * 
 * Orchestrator-komponentti joka kokoaa tilauslistanäkymän.
 * Varsinainen logiikka on eriytetty hook- ja komponenttitiedostoihin.
 */

import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OrderDialog } from "@/components/orders/OrderDialog";
import { useOrdersQuery } from "@/hooks/useOrdersQuery";
import { useDebounce } from "@/hooks/useDebounce";
 import { useOrderStatusMutation } from "@/hooks/useOrderStatusMutation";
import {
  OrdersHeader,
  OrdersFilters,
  OrdersTable,
  CancelOrderDialog,
} from "@/components/orders/list";
import {
  DEFAULT_ORDER_FILTERS,
  type OrderFilters,
  type OrderTabType,
  type OrderListItem,
} from "@/types/orders";

export default function Orders() {
  const navigate = useNavigate();
  
  // State
  const [filters, setFilters] = useState<OrderFilters>(DEFAULT_ORDER_FILTERS);
  const [activeTab, setActiveTab] = useState<OrderTabType>("active");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editOrderId, setEditOrderId] = useState<string | undefined>(undefined);
  const [orderToCancel, setOrderToCancel] = useState<OrderListItem | null>(null);

  // Debounce hakusana (300ms viive)
  const debouncedSearch = useDebounce(filters.search, 300);
  const searchFilters = useMemo(
    () => ({ ...filters, search: debouncedSearch }),
    [filters, debouncedSearch]
  );

  // Data - käytä debounced-hakua queryssa
  const { 
    data: orders, 
    isLoading,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedCount,
  } = useOrdersQuery(searchFilters, activeTab);
   const cancelMutation = useOrderStatusMutation();

  // Handlers
  const toggleRow = useCallback((orderId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  }, []);

  const handleCreateOrder = useCallback(() => {
    setEditOrderId(undefined);
    setIsDialogOpen(true);
  }, []);

  const handleEditOrder = useCallback((orderId: string) => {
    setEditOrderId(orderId);
    setIsDialogOpen(true);
  }, []);

  const handleCancelConfirm = useCallback(
    (orderId: string) => {
       cancelMutation.mutate({
         orderId,
         newStatus: "cancelled",
       });
      setOrderToCancel(null);
    },
    [cancelMutation]
  );

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return (
    <Layout>
      <div className="space-y-6">
        <OrdersHeader onCreateOrder={handleCreateOrder} />

        <OrderDialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) setEditOrderId(undefined);
          }}
          orderId={editOrderId}
        />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as OrderTabType)}>
          <TabsList>
            <TabsTrigger value="active" className="gap-2">
              Aktiiviset
              {activeTab === "active" && (
                <Badge variant="secondary" className="ml-1">
                  {totalCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="archive" className="gap-2">
              Arkisto
              {activeTab === "archive" && (
                <Badge variant="secondary" className="ml-1">
                  {totalCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="deleted" className="gap-2">
              Poistetut
              {activeTab === "deleted" && (
                <Badge variant="secondary" className="ml-1">
                  {totalCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardContent className="pt-6">
                <OrdersFilters filters={filters} onFiltersChange={setFilters} />
                <OrdersTable
                  orders={orders}
                  isLoading={isLoading}
                  activeTab={activeTab}
                  expandedRows={expandedRows}
                  onToggleRow={toggleRow}
                  onEditOrder={handleEditOrder}
                  onCancelOrder={setOrderToCancel}
                  onCreateOrder={handleCreateOrder}
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={handleLoadMore}
                  loadedCount={loadedCount}
                  totalCount={totalCount}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <CancelOrderDialog
          order={orderToCancel}
          onClose={() => setOrderToCancel(null)}
          onConfirm={handleCancelConfirm}
        />
      </div>
    </Layout>
  );
}
